// Onboarding-mode tool definitions (PLAN.md §25, D-58). These WRITE the patient's
// standing configuration — regimens, day-part times, and basic profile — NOT
// events. Onboarding mode also reuses RECORD_ILLNESS_TOOL and
// RECORD_FAMILY_HISTORY_TOOL (conditions + family history are event types).
// Pure data — safe for both API (Lambda) and web SPA.

import { MED_UNITS, MED_ROUTES, SCHEDULED_GROUPS } from "./medication.js";
import { PATIENT_PROFILE_FIELDS } from "./types.js";

/**
 * Create or replace a scheduled regimen (a named group of meds taken together at
 * a day-part). If a regimen with the same name exists, it is replaced.
 */
export const UPSERT_REGIMEN_TOOL = {
  name: "upsert_regimen",
  description:
    "Create or update a SCHEDULED regimen — a named group of medications the patient takes together " +
    "at a particular time of day (e.g. 'Morning pills'). Provide every medication with its name and " +
    "strength: either a numeric dose + unit for a single-ingredient med, or an 'ingredients' breakdown " +
    "for a combination product. If the dose/strength is missing, ask the patient for it before calling " +
    "this. If something the patient lists doesn't sound like a medication, ask what it is rather than guessing.",
  input_schema: {
    type: "object",
    properties: {
      name: { type: "string", description: "Regimen name, e.g. 'Morning pills'." },
      scheduledGroup: { type: "string", enum: [...SCHEDULED_GROUPS], description: "Day-part this group is taken." },
      defaultTime: { type: "string", description: "Local 'HH:MM' the patient usually takes it, if known." },
      items: {
        type: "array",
        description: "The medications in this regimen.",
        items: {
          type: "object",
          properties: {
            name: { type: "string" },
            dose: {
              type: "number",
              description: "Numeric dose for a single-ingredient med. Omit and use 'ingredients' for a combination product.",
            },
            unit: { type: "string", enum: [...MED_UNITS] },
            ingredients: {
              type: "array",
              description:
                "Active-ingredient breakdown for a COMBINATION product. Provide INSTEAD of dose/unit. " +
                "NEVER add the doses of different ingredients together.",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  dose: { type: "number" },
                  unit: { type: "string", enum: [...MED_UNITS] },
                },
                required: ["name", "dose", "unit"],
              },
            },
            route: { type: "string", enum: [...MED_ROUTES] },
            intendedFor: { type: "string", description: "What it's for, if the patient says." },
          },
          required: ["name"],
        },
      },
    },
    required: ["name", "scheduledGroup", "items"],
  },
} as const;

/** Set one or more of the patient's day-part time preferences (D-47). */
export const SET_DAY_PART_TIMES_TOOL = {
  name: "set_day_part_times",
  description:
    "Set the patient's personal clock times for day-part words (e.g. their 'breakfast' is 07:00, " +
    "'bedtime' is 22:30). Only include the day-parts the patient actually specified. Times are local 'HH:MM'.",
  input_schema: {
    type: "object",
    properties: Object.fromEntries(
      SCHEDULED_GROUPS.filter((g) => g !== "other").map((g) => [
        g,
        { type: "string", description: `Local HH:MM for ${g}.` },
      ])
    ),
  },
} as const;

/** Set basic standing-profile fields (D-63). */
export const SET_PATIENT_PROFILE_TOOL = {
  name: "set_patient_profile",
  description:
    "Set basic standing profile facts about the patient that help later analysis — date of birth, sex, " +
    "timezone, height, blood type, smoking status, alcohol use, activity level, diet pattern, primary " +
    "provider, pharmacy, and their goals (what they hope to figure out). Only include fields the patient " +
    "actually gives.",
  input_schema: {
    type: "object",
    properties: {
      dob: { type: "string", description: "ISO-8601 date of birth." },
      sex: { type: "string", enum: ["female", "male", "intersex", "unspecified"] },
      timezone: { type: "string", description: "IANA timezone, e.g. America/New_York." },
      heightCm: { type: "number", description: "Height in centimeters." },
      bloodType: { type: "string", description: "e.g. O+, A-, etc." },
      smokingStatus: { type: "string", enum: ["never", "former", "current", "unknown"] },
      alcoholUse: { type: "string", description: "Free text, e.g. 'occasional', 'none'." },
      activityLevel: { type: "string", enum: ["sedentary", "light", "moderate", "active"] },
      dietPattern: { type: "string", description: "e.g. vegetarian, low-carb." },
      primaryProvider: { type: "string" },
      pharmacy: { type: "string" },
      goals: { type: "string", description: "What the patient hopes to track or figure out." },
    },
  },
} as const;

/** The settable profile field names (re-export for the validator). */
export const ONBOARDING_PROFILE_FIELDS = PATIENT_PROFILE_FIELDS;
